/* eslint-disable graphql/no-deprecated-fields */
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import { flatMap } from 'lodash';

import { accountsQuery as AccountsQueryData } from './types/accountsQuery';
import { accountPlanQuery as AccountQueryData } from './types/accountPlanQuery';
import { pricingPageV3Query as PricingPageV3QueryData } from './types/pricingPageV3Query';

const planFragment = gql`
  fragment planFragment on PlanV2 {
    id
    allowCompanies
    amount
    description
    features {
      href
      name
      tooltip
    }
    hasAutoReplay
    hasPaths
    hasMeteredBilling
    hasMultiStepZaps
    hasPremiumApps
    hasTeams
    interval
    isInPlanGroup
    name
    tasks
    tiers {
      numTasks
      pricePerTask
    }
    type
    zaps
    zapsRunInterval
  }
`;

export const pricingPageV3Query = gql`
  query pricingPageV3Query($accountId: Int) {
    plans: pricingPageV3Plans(accountId: $accountId) {
      id
      planType
      plans {
        ...planFragment
      }
    }
  }
  ${planFragment}
`;

export const accountQuery = gql`
  query accountPlanQuery($accountId: Int!) {
    account(id: $accountId) {
      companiesTrialEndDate
      companyAccountEndDate
      companyName
      currentUserRole
      hasBeenInCompaniesTrial
      id
      isPaying
      isTeamAccount
      isTrialing
      isTrialingTeams
      isTrialingCompanies
      hasBeenInCompaniesTrial
      name
      owner {
        id
        email
      }
      plan: planV2 {
        ...planFragment
      }
      switches
      teamAccountEndDate
      teamTrialEndDate
    }
  }
  ${planFragment}
`;

export const accountsQuery = gql`
  query accountsQuery {
    currentUser: currentUserV2 {
      id
      email
      fullName
    }

    accounts {
      id
      plan {
        id
        name
      }
    }
  }
`;

type Options = {
  // Returns the plans in a flat array, rather than grouped by plan type
  flatPlans?: boolean;
};

export const usePlans = (accountId?: number, options: Options = {}) => {
  // Query for account details
  const accountsResults = useQuery<AccountsQueryData>(accountsQuery, {
    fetchPolicy: 'network-only',
  });
  const accountResults = useQuery<AccountQueryData>(accountQuery, {
    fetchPolicy: 'network-only',
    skip: !accountId,
    variables: { accountId },
  });

  const currentAccount = accountResults?.data?.account;

  // Query for plans
  const planResults = useQuery<PricingPageV3QueryData>(pricingPageV3Query, {
    variables: {
      accountId,
    },
  });

  const result = {
    error:
      accountResults.error || planResults.error
        ? 'There was an issue loading data. Please try again'
        : null,
    isLoading: accountResults.loading || planResults.loading,
    data: {
      currentAccount: undefined,
      hasTeamOrProfessionalPlan: false,
      isTrialing: false,
      isTrialingTeams: false,
      isTrialingCompanies: false,
      currentPlan: undefined,
      isOnLegacyPlan: false,
      isPaid: false,
      plans: [],
      userEmail: undefined,
    },
  };

  if (!planResults.data || !accountsResults.data) {
    return result;
  } else {
    const { currentUser, accounts } = accountsResults.data;
    const { plans } = planResults.data;

    const types = accounts
      .map(account => account.plan.name)
      .filter(Boolean)
      .map(name => name.toLowerCase());

    const hasTeamOrProfessionalPlan =
      !types.some(t => t.includes('company')) &&
      types.some((t: string) => t.includes('team') || t.includes('pro'));

    const currentPlan = currentAccount?.plan;

    const data = {
      currentAccount,
      hasTeamOrProfessionalPlan,
      isTrialing: Boolean(currentAccount?.isTrialing),
      isTrialingTeams: Boolean(currentAccount?.isTrialingTeams),
      isTrialingCompanies: Boolean(currentAccount?.isTrialingCompanies),
      currentPlan,
      isOnLegacyPlan: !currentPlan?.isInPlanGroup,
      isPaid: Boolean(currentAccount?.isPaying),
      plans: options.flatPlans
        ? flatMap(plans, planStack => planStack.plans)
        : plans,
      userEmail: currentUser?.email,
      userId: currentUser?.id,
      userName: currentUser?.fullName,
      userRole: currentAccount?.currentUserRole,
    };

    return { ...result, data };
  }
};
